import React from 'react';
import { Container } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import AboutJsonLd from './AboutJsonLd';
import './AboutPage.css';

/** Long-form About / FAQ content (also linked from footer as “Sobre”). */
const AboutPage: React.FC = () => {
  const { t, ready } = useSafeTranslation();

  const safeT = (key: string, fallback: string) => t(key, fallback);

  if (!ready) {
    return (
      <div className="about-page about-page--loading">
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="about-page">
      <AboutJsonLd />
      <Container fluid className="about-page-inner px-3 px-md-4 py-4">
        <nav className="about-back-nav" aria-label={safeT('seo.about.navAria', 'About page')}>
          <Link to="/" className="about-back-link">
            ← {safeT('seo.about.backHome', 'Back to home')}
          </Link>
        </nav>

        <article className="about-marketing-section" aria-labelledby="about-main-heading">
          <h1 id="about-main-heading" className="about-marketing-title">
            {safeT('seo.home.sectionAbout', 'About Quizter Club')}
          </h1>
          <p className="about-marketing-p">{safeT('seo.home.leadP1', '')}</p>
          <p className="about-marketing-p">{safeT('seo.home.leadP2', '')}</p>
          <p className="about-marketing-p">{safeT('seo.home.leadP3', '')}</p>
          <p className="about-marketing-p">{safeT('seo.home.leadP4', '')}</p>
          <p className="about-marketing-p">{safeT('seo.home.leadP5', '')}</p>

          <h2 className="about-marketing-subtitle">{safeT('seo.home.statsHeading', 'By the numbers')}</h2>
          <p className="about-marketing-p">{safeT('seo.home.statsIntro', '')}</p>
          <ul className="about-marketing-list">
            <li>{safeT('seo.home.statLi1', '')}</li>
            <li>{safeT('seo.home.statLi2', '')}</li>
            <li>{safeT('seo.home.statLi3', '')}</li>
            <li>{safeT('seo.home.statLi4', '')}</li>
          </ul>

          <h2 className="about-marketing-subtitle">{safeT('seo.home.modesHeading', 'Modes at a glance (ordered)')}</h2>
          <ol className="about-marketing-ol">
            <li>{safeT('seo.home.modeOl1', '')}</li>
            <li>{safeT('seo.home.modeOl2', '')}</li>
            <li>{safeT('seo.home.modeOl3', '')}</li>
          </ol>

          <h2 className="about-marketing-subtitle">{safeT('seo.home.bulletsHeading', 'Why players stick around')}</h2>
          <ul className="about-marketing-list">
            <li>{safeT('seo.home.bullet1', '')}</li>
            <li>{safeT('seo.home.bullet2', '')}</li>
            <li>{safeT('seo.home.bullet3', '')}</li>
            <li>{safeT('seo.home.bullet4', '')}</li>
          </ul>

          <h2 className="about-marketing-subtitle" id="about-faq-heading">
            {safeT('seo.home.faqHeading', 'Frequently asked questions')}
          </h2>
          <dl className="about-marketing-faq">
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <React.Fragment key={n}>
                <dt>{safeT(`seo.home.faqQ${n}`, '')}</dt>
                <dd>{safeT(`seo.home.faqA${n}`, '')}</dd>
              </React.Fragment>
            ))}
          </dl>
        </article>
      </Container>
    </div>
  );
};

export default AboutPage;
